import {
  appModules,
  FULL_ACCESS_ROLES,
  MODULE_KEYS,
  PAGE_KEYS,
  ACTION_KEYS,
} from "@/config/moduleRegistry";
import type {
  AccessMatrix,
  AccessSnapshot,
  AccessSnapshotSource,
  PermissionActionEntry,
  PermissionManifestEntry,
} from "@/type/permissions";
import type { AppActionDefinition, AppModuleDefinition, AppPageDefinition } from "@/config/moduleRegistry";

export {
  ACTION_KEYS,
  FULL_ACCESS_ROLES,
  MODULE_KEYS,
  PAGE_KEYS,
};

function toLabel(value: { label?: string; labelKey: string }) {
  return value.label ?? value.labelKey;
}

function moduleToManifestEntry(module: AppModuleDefinition): PermissionManifestEntry {
  return {
    key: module.key,
    label: toLabel(module),
    fallbackRoles: module.fallbackRoles,
  };
}

function pageToManifestEntry(
  page: AppPageDefinition,
  module: AppModuleDefinition,
): PermissionManifestEntry {
  const paths = page.paths ?? (page.path ? [page.path] : undefined);

  return {
    key: page.key,
    label: toLabel(page),
    doctype: page.doctype,
    moduleKey: module.key,
    fallbackRoles: page.fallbackRoles,
    paths,
    pathPrefixes: page.pathPrefixes,
    routeNames: page.routeNames,
  };
}

function actionToManifestEntry(
  action: AppActionDefinition,
  module: AppModuleDefinition,
): PermissionActionEntry {
  return {
    key: action.key,
    label: toLabel(action),
    moduleKey: module.key,
    pageKey: action.pageKey,
    fallbackRoles: action.fallbackRoles,
  };
}

export const permissionModules: PermissionManifestEntry[] = appModules.map(moduleToManifestEntry);

export const permissionPages: PermissionManifestEntry[] = appModules
  .flatMap(module => module.pages.map(page => pageToManifestEntry(page, module)));

export const permissionActions: PermissionActionEntry[] = appModules
  .flatMap(module => (module.actions ?? []).map(action => actionToManifestEntry(action, module)));

export const permissionManifest: AccessMatrix = {
  modules: permissionModules,
  pages: permissionPages,
  actions: permissionActions,
};

export function getDefaultAccessMatrix(): AccessMatrix {
  return {
    modules: [...permissionModules],
    pages: [...permissionPages],
    actions: [...permissionActions],
  };
}

export function hasFullAccessRole(roles: string[]) {
  const fullAccessRoleSet = new Set(
    FULL_ACCESS_ROLES.map(role => String(role).trim().toLowerCase()),
  );

  return roles.some(role => fullAccessRoleSet.has(String(role).trim().toLowerCase()));
}

function entryAllowsRole(entry: PermissionManifestEntry, roles: string[]) {
  if (hasFullAccessRole(roles))
    return true;

  const fallbackRoles = entry.fallbackRoles ?? [];

  return fallbackRoles.some(role => roles.includes(role));
}

function unique(values: string[]) {
  return [...new Set(values.filter(Boolean))];
}

export function isPermissionRoleFallbackEnabled() {
  return ["1", "true", "yes", "on"].includes(
    String(import.meta.env.VITE_PERMISSION_ROLE_FALLBACK ?? "").trim().toLowerCase(),
  );
}

export function buildDeniedAccessSnapshotFromRoles(
  roles: string[],
  user: Record<string, any> | null = null,
): AccessSnapshot {
  const normalizedRoles = unique(roles);
  const roleProfile = user?.role_profile_name ?? user?.role_profile ?? user?.roleProfile ?? null;

  return {
    roles: normalizedRoles,
    roleProfile,
    modules: [],
    pages: [],
    actions: [],
    doctypes: {},
    restrictions: {},
    source: "empty",
    loadedAt: new Date().toISOString(),
  };
}

export function buildAccessSnapshotFromRoles(
  roles: string[],
  user: Record<string, any> | null = null,
  source: AccessSnapshotSource = roles.length ? "roles-fallback" : "empty",
): AccessSnapshot {
  const normalizedRoles = unique(roles);
  const roleProfile = user?.role_profile_name ?? user?.role_profile ?? user?.roleProfile ?? null;

  return {
    roles: normalizedRoles,
    roleProfile,
    modules: unique(permissionModules.filter(entry => entryAllowsRole(entry, normalizedRoles)).map(entry => entry.key)),
    pages: unique(permissionPages.filter(entry => entryAllowsRole(entry, normalizedRoles)).map(entry => entry.key)),
    actions: unique(permissionActions.filter(entry => entryAllowsRole(entry, normalizedRoles)).map(entry => entry.key)),
    restrictions: {},
    source,
    loadedAt: new Date().toISOString(),
  };
}

export function getPageKeyForRoute(path: string, routeName?: string | symbol | null) {
  const name = routeName ? String(routeName) : "";

  const nameMatch = permissionPages.find(entry => entry.routeNames?.includes(name));
  if (nameMatch)
    return nameMatch.key;

  const exactMatch = permissionPages.find(entry => entry.paths?.includes(path));
  if (exactMatch)
    return exactMatch.key;

  const prefixMatches = permissionPages
    .filter(entry => entry.pathPrefixes?.some(prefix => path.startsWith(prefix)))
    .sort((a, b) => {
      const aLongest = Math.max(...(a.pathPrefixes ?? []).map(prefix => prefix.length));
      const bLongest = Math.max(...(b.pathPrefixes ?? []).map(prefix => prefix.length));

      return bLongest - aLongest;
    });

  return prefixMatches[0]?.key ?? null;
}

export function getDoctypeForPageKey(key: string) {
  return permissionPages.find(entry => entry.key === key)?.doctype ?? null;
}

export function getPageDoctypeForRoute(path: string, routeName?: string | symbol | null) {
  const key = getPageKeyForRoute(path, routeName);

  return key ? getDoctypeForPageKey(key) : null;
}

export function getDoctypesForModuleKey(key: string) {
  return permissionPages
    .filter(entry => entry.moduleKey === key)
    .map(entry => entry.doctype)
    .filter(Boolean) as string[];
}

export function getPageKeysForModuleKey(key: string) {
  return permissionPages
    .filter(entry => entry.moduleKey === key)
    .map(entry => entry.key);
}

export function getManifestEntry(key: string) {
  return [
    ...permissionModules,
    ...permissionPages,
    ...permissionActions,
  ].find(entry => entry.key === key) ?? null;
}
